"use client";

import { TRANSPOSE_RANGE } from "./tonality-picker-helpers";

type Props = {
  value: number;
  onChange: (v: number) => void;
  appliedSemis: number;
  disabled?: boolean;
};

export function TonalityPickerTickSlider({
  value,
  onChange,
  appliedSemis,
  disabled,
}: Props) {
  const total = TRANSPOSE_RANGE * 2;
  const valuePct = ((value + TRANSPOSE_RANGE) / total) * 100;
  const appliedPct = ((appliedSemis + TRANSPOSE_RANGE) / total) * 100;
  const dirty = value !== appliedSemis;

  return (
    <div
      className={`relative mb-1 ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      style={{ height: 22 }}
    >
      {/* Base track */}
      <div
        className="absolute inset-x-0 rounded-full"
        style={{ top: 10, height: 2, background: "#1a1a22" }}
      />

      {/* Tick marks */}
      {Array.from({ length: total + 1 }).map((_, i) => {
        const semis = i - TRANSPOSE_RANGE;
        const isOctave = semis % 12 === 0;
        const left = (i / total) * 100;

        return (
          <div
            key={i}
            className="absolute -translate-x-1/2"
            style={{
              left: `${left}%`,
              top: isOctave ? 5 : 8,
              width: 1,
              height: isOctave ? 12 : 6,
              background: isOctave
                ? "var(--color-text-secondary)"
                : "var(--color-text-muted)",
            }}
          />
        );
      })}

      {/* Applied (ghost) marker, only when staged differs from applied */}
      {dirty && (
        <div
          className="absolute -translate-x-1/2 rounded-full"
          style={{
            left: `${appliedPct}%`,
            top: 7,
            width: 8,
            height: 8,
            background: "transparent",
            border: "1.5px solid var(--color-text-muted)",
          }}
        />
      )}

      {/* Progress fill (from 0 toward value) */}
      {value !== 0 && (
        <div
          className="absolute rounded-full"
          style={{
            top: 10,
            height: 2,
            left: value >= 0 ? "50%" : `${valuePct}%`,
            width:
              value >= 0
                ? `${(value / total) * 100}%`
                : `${(-value / total) * 100}%`,
            background: "var(--color-accent)",
          }}
        />
      )}

      {/* Thumb */}
      <div
        className="absolute -translate-x-1/2 rounded-full"
        style={{
          left: `${valuePct}%`,
          top: 4,
          width: 14,
          height: 14,
          background: "var(--color-accent)",
          boxShadow: "0 0 12px rgba(139, 92, 246, 0.6)",
        }}
      />

      {/* Hidden range input on top for interaction */}
      <input
        type="range"
        min={-TRANSPOSE_RANGE}
        max={TRANSPOSE_RANGE}
        step={1}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        aria-label="Demi-tons"
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      />
    </div>
  );
}
